import { useEffect, useMemo, useState } from "react";
import {
  Diamond,
  ArrowDownRight,
  ArrowUpRight,
  Plus,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
} from "lucide-react";
import { useBalanceQuery, useTransactionsQuery, type Transaction } from "@/lib/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SelectNative } from "@/components/ui/select-native";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RechargeModal } from "@/components/billing/RechargeModal";
import { transactionLabel } from "@/lib/format";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 10;
type ActionFilter = "" | "topup" | "consume" | "refund";

export function BillingView() {
  const balance = useBalanceQuery();
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [actionFilter, setActionFilter] = useState<ActionFilter>("");
  const [page, setPage] = useState(1);

  const filters = useMemo(
    () => ({
      action: actionFilter,
      from: toStartIso(fromDate),
      to: toNextDayIso(toDate),
    }),
    [actionFilter, fromDate, toDate],
  );
  const { items, isLoading } = useTransactionsQuery(filters);

  useEffect(() => {
    setPage(1);
  }, [fromDate, toDate, actionFilter]);

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = items.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const hasFilters = Boolean(fromDate || toDate || actionFilter);

  return (
    <div className="mx-auto max-w-3xl px-6 py-6">
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">点数流水</h1>
          <p className="text-sm text-muted-foreground">所有充值、消费、退费</p>
        </div>
        <Button onClick={() => setRechargeOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          充值
        </Button>
      </header>

      <RechargeModal open={rechargeOpen} onOpenChange={setRechargeOpen} />

      {balance.data && (
        <div className="mb-6 grid grid-cols-3 gap-3">
          <BalanceCard label="账户余额" value={balance.data.credit_balance} accent />
          <BalanceCard
            label="今日已用"
            value={balance.data.daily_consumed}
          />
          <BalanceCard
            label="历史已用"
            value={balance.data.lifetime_consumed}
          />
        </div>
      )}

      <div className="mb-3 rounded-lg border border-border bg-card px-4 py-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(220px,1fr)_140px_auto] sm:items-end">
          <DateRangeFilter
            fromDate={fromDate}
            toDate={toDate}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
          <FilterField label="行为">
            <SelectNative
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value as ActionFilter)}
            >
              <option value="">全部</option>
              <option value="topup">充值</option>
              <option value="refund">退费</option>
              <option value="consume">消费</option>
            </SelectNative>
          </FilterField>
          <Button
            variant="ghost"
            size="sm"
            disabled={!hasFilters}
            onClick={() => {
              setFromDate("");
              setToDate("");
              setActionFilter("");
            }}
          >
            清除
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-sm text-muted-foreground">
          {hasFilters ? "没有匹配的流水" : "还没有任何流水"}
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {pageItems.map((tx) => (
              <TxnRow key={tx.transaction_id} tx={tx} />
            ))}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>
              第 {currentPage} / {pageCount} 页 · 共 {items.length} 条
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                上一页
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={currentPage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                下一页
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DateRangeFilter({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}: {
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
}) {
  const [viewMonth, setViewMonth] = useState(() =>
    startOfMonth(parseDateString(fromDate) ?? new Date()),
  );
  const calendarDays = useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const monthLabel = `${viewMonth.getFullYear()} 年 ${viewMonth.getMonth() + 1} 月`;

  const selectDate = (date: string) => {
    if (!fromDate || toDate) {
      onFromDateChange(date);
      onToDateChange("");
      return;
    }
    if (date < fromDate) {
      onFromDateChange(date);
      return;
    }
    onToDateChange(date);
  };

  const setQuickRange = (days: number) => {
    const end = new Date();
    const start = addDays(end, -(days - 1));
    onFromDateChange(toDateString(start));
    onToDateChange(toDateString(end));
    setViewMonth(startOfMonth(end));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">
        查询时间
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="h-10 w-full justify-between border-border bg-background px-3 font-normal"
          >
            <span className="truncate text-left">
              {formatDateRangeLabel(fromDate, toDate)}
            </span>
            <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[336px] p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
                onClick={() => setViewMonth(addMonths(viewMonth, -1))}
                aria-label="上个月"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <strong className="text-sm font-semibold text-foreground">
                {monthLabel}
              </strong>
              <button
                type="button"
                className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-white/[0.04] hover:text-foreground"
                onClick={() => setViewMonth(addMonths(viewMonth, 1))}
                aria-label="下个月"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground">
              {["一", "二", "三", "四", "五", "六", "日"].map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day) => {
                const isStart = day.date === fromDate;
                const isEnd = day.date === toDate;
                const inRange =
                  Boolean(fromDate && toDate) &&
                  day.date >= fromDate &&
                  day.date <= toDate;
                return (
                  <button
                    key={day.date}
                    type="button"
                    onClick={() => selectDate(day.date)}
                    className={cn(
                      "h-9 rounded-md text-sm tabular-nums transition-colors",
                      day.inMonth
                        ? "text-foreground"
                        : "text-muted-foreground/35",
                      inRange && "bg-accent/12 text-foreground",
                      (isStart || isEnd) &&
                        "bg-accent font-semibold text-accent-foreground hover:bg-accent",
                      !isStart &&
                        !isEnd &&
                        "hover:bg-white/[0.05] hover:text-foreground",
                    )}
                  >
                    {day.day}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setQuickRange(1)}>
                今天
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setQuickRange(7)}>
                近 7 天
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={() => setQuickRange(30)}>
                近 30 天
              </Button>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!fromDate && !toDate}
              onClick={() => {
                onFromDateChange("");
                onToDateChange("");
              }}
            >
              清空时间
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground/85">
        {label}
      </span>
      {children}
    </label>
  );
}

function BalanceCard({
  label,
  value,
  unit,
  hint,
  accent,
}: {
  label: string;
  value: number | string;
  unit?: string;
  hint?: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 flex items-baseline gap-1 font-mono text-2xl font-semibold tabular-nums",
          accent ? "text-accent" : "text-foreground",
        )}
      >
        {value}
        {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
      </p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function TxnRow({ tx }: { tx: Transaction }) {
  const isCredit = tx.amount > 0;
  return (
    <li className="flex items-center justify-between gap-4 p-4 transition-colors hover:bg-white/[0.02]">
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-md",
            isCredit
              ? "bg-success/10 text-success"
              : "bg-destructive/10 text-destructive"
          )}
        >
          {isCredit ? (
            <ArrowDownRight className="h-4 w-4" />
          ) : (
            <ArrowUpRight className="h-4 w-4" />
          )}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {transactionLabel(tx.transaction_type)}
            </span>
            {tx.related_job_id && (
              <Badge variant="outline" className="font-mono text-[10px]">
                #{tx.related_job_id}
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date(tx.created_at).toLocaleString("zh-CN")}
            {tx.remark ? ` · ${transactionRemarkLabel(tx.remark)}` : ""}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div
          className={cn(
            "flex items-center gap-1 font-mono text-base font-semibold tabular-nums",
            isCredit ? "text-success" : "text-foreground"
          )}
        >
          <span>{isCredit ? "+" : ""}{tx.amount}</span>
          <Diamond className="h-3 w-3 text-accent" />
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <div>余额</div>
          <div className="font-mono tabular-nums text-foreground/85">
            {tx.balance_after}
          </div>
        </div>
      </div>
    </li>
  );
}

function toStartIso(date: string) {
  if (!date) return undefined;
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toNextDayIso(date: string) {
  if (!date) return undefined;
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

function transactionRemarkLabel(remark: string) {
  const exact: Record<string, string> = {
    free_creation: "自由创作",
    fashion_model_generation: "服装模特",
    product_scene_generation: "商品场景",
    "demo seed credits": "测试账号初始积分",
    "new user bonus credits": "新用户赠送积分",
    "free_creation provider failure": "自由创作失败自动退费",
    "job canceled by user": "任务取消自动退费",
  };
  if (exact[remark]) return exact[remark];
  if (remark.includes("provider failure")) return "生成失败自动退费";
  if (remark.includes("failed job") || remark.includes("empty result")) {
    return "生成失败自动退费";
  }
  if (remark.includes("canceled") || remark.includes("cancelled")) {
    return "任务取消自动退费";
  }
  return remark;
}

function formatDateRangeLabel(fromDate: string, toDate: string) {
  if (fromDate && toDate) {
    if (fromDate === toDate) return fromDate;
    return `${fromDate} 至 ${toDate}`;
  }
  if (fromDate) return `${fromDate} 起`;
  if (toDate) return `截至 ${toDate}`;
  return "全部时间";
}

function parseDateString(value: string) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toDateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, count: number) {
  return new Date(date.getFullYear(), date.getMonth() + count, 1);
}

function addDays(date: Date, count: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

function buildCalendarDays(month: Date) {
  const first = startOfMonth(month);
  const offset = (first.getDay() + 6) % 7;
  const start = addDays(first, -offset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(start, index);
    return {
      date: toDateString(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month.getMonth(),
    };
  });
}
